import { EditorState } from 'draft-js';
import React, { useState, useRef, useCallback, useMemo } from 'react';

function _extends() {
  // eslint-disable-next-line
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var _excluded = ["blockProps", "isResizable", "resizeSteps"];

var getDisplayName = function getDisplayName(WrappedComponent) {
  var component = WrappedComponent.WrappedComponent || WrappedComponent;
  return component.displayName || component.name || 'Component';
};

var round = function round(x, steps) {
  return Math.ceil(x / steps) * steps;
};

var createDecorator = (function (_ref) {
  var config = _ref.config,
    store = _ref.store;
  return function (WrappedComponent) {
    var BlockResizeableDecorator = /*#__PURE__*/React.forwardRef(function (_ref2, _ref3) {
      var blockProps = _ref2.blockProps,
        _ref2$isResizable = _ref2.isResizable,
        isResizable = _ref2$isResizable === void 0 ? true : _ref2$isResizable,
        _ref2$resizeSteps = _ref2.resizeSteps,
        resizeSteps = _ref2$resizeSteps === void 0 ? 1 : _ref2$resizeSteps,
        elementProps = _objectWithoutPropertiesLoose(_ref2, _excluded);

      var _config$vertical = config.vertical,
        vertical = _config$vertical === void 0 ? false : _config$vertical,
        _config$horizontal = config.horizontal,
        horizontal = _config$horizontal === void 0 ? 'relative' : _config$horizontal,
        initialWidth = config.initialWidth,
        initialHeight = config.initialHeight;

      var _useState = useState(false),
        clicked = _useState[0],
        setClicked = _useState[1];

      var _useState2 = useState(0),
        width = _useState2[0],
        setWidth = _useState2[1];

      var _useState3 = useState(0),
        height = _useState3[0],
        setHeight = _useState3[1];

      var _useState4 = useState({}),
        hoverPosition = _useState4[0],
        setHoverPosition = _useState4[1];

      var wrapper = useRef();

      var mouseLeave = useCallback(function () {
        if (!clicked) {
          setHoverPosition({});
        }
      }, [clicked, setHoverPosition]);

      var mouseMove = useCallback(function (evt) {
        var tolerance = 6;
        var b = wrapper.current.getBoundingClientRect();
        var x = evt.clientX - b.left;
        var y = evt.clientY - b.top;
        var isTop = vertical && vertical !== 'auto' ? y < tolerance : false;
        var isLeft = horizontal ? x < tolerance : false;
        var isRight = horizontal ? x >= b.width - tolerance : false;
        var isBottom = vertical && vertical !== 'auto' ? y >= b.height - tolerance && y < b.height : false;
        var canResize = (isTop || isLeft || isRight || isBottom) && isResizable;
        var newHoverPosition = {
          isTop: isTop,
          isLeft: isLeft,
          isRight: isRight,
          isBottom: isBottom,
          canResize: canResize
        };
        setHoverPosition(function (oldHoverPosition) {
          var hasNewHoverPositions = Object.keys(newHoverPosition).filter(function (key) {
            return oldHoverPosition[key] !== newHoverPosition[key];
          });

          if (hasNewHoverPositions.length) {
            return newHoverPosition;
          }

          return oldHoverPosition;
        });
      }, [vertical, horizontal, isResizable, setHoverPosition]);

      var mouseDown = useCallback(function (event) {
        // No mouse-hover-position data? Nothing to resize!
        //if (!hoverPosition.canResize) {
        //  return;
        //}

        event.preventDefault();
        var isTop = hoverPosition.isTop,
          isLeft = hoverPosition.isLeft,
          isRight = hoverPosition.isRight,
          isBottom = hoverPosition.isBottom;
        var pane = wrapper.current;
        var startX = event.clientX;
        var startY = event.clientY;
        var startWidth = parseInt(document.defaultView.getComputedStyle(pane).width, 10);
        var startHeight = parseInt(document.defaultView.getComputedStyle(pane).height, 10);
        var newWidth = width;
        var newHeight = height; // Do the actual drag operation

        var doDrag = function doDrag(dragEvent) {

          var _width = startWidth + (isLeft ? startX - dragEvent.clientX : dragEvent.clientX - startX);
          var _height = startHeight + dragEvent.clientY - startY;

          var editorComp = store.getEditorRef(); // this keeps backwards-compatibility with react 15

          var editorNode = editorComp.refs && editorComp.refs.editor ? editorComp.refs.editor : editorComp.editor;
          _width = Math.min(editorNode.clientWidth, _width);
          _height = Math.min(editorNode.clientHeight, _height);
          var widthPerc = 100 / editorNode.clientWidth * _width;
          var heightPerc = 100 / editorNode.clientHeight * _height;

          if ((isLeft || isRight) && horizontal === 'relative') {
            newWidth = resizeSteps ? round(widthPerc, resizeSteps) : widthPerc;
            setWidth(newWidth);
          } else if ((isLeft || isRight) && horizontal === 'absolute') {
            newWidth = resizeSteps ? round(_width, resizeSteps) : _width;
            setWidth(newWidth);
          }

          if ((isTop || isBottom) && vertical === 'relative') {
            newHeight = resizeSteps ? round(heightPerc, resizeSteps) : heightPerc;
            setHeight(newHeight);
          } else if ((isTop || isBottom) && vertical === 'absolute') {
            newHeight = resizeSteps ? round(_height, resizeSteps) : _height;
            setHeight(newHeight);
          }

          dragEvent.preventDefault();
        }; // Finished dragging

        var doDrag2 = function doDrag2(dragEvent) {

          var editorComp = store.getEditorRef(); // this keeps backwards-compatibility with react 15
          var editorNode = editorComp.refs && editorComp.refs.editor ? editorComp.refs.editor : editorComp.editor;

          var pw =  Math.ceil(startWidth * 100 / editorNode.clientWidth);

          if(pw >= 25 && pw < 50){
            pw = 50
          }else if(pw >= 50 && pw < 75){
            pw = 75
          }else if(pw >= 75 && pw < 100){
            pw = 100
          }else{
            pw = 25
          };
          
          setWidth(pw);

          dragEvent.preventDefault();
        }; // Finished dragging


        var stopDrag = function stopDrag() {
          // TODO clean up event listeners
          document.removeEventListener('mousemove', doDrag, false);
          document.removeEventListener('mouseup', stopDrag, false);
          setClicked(false);
          blockProps.setResizeData({
            width: newWidth,
            height: newHeight
          });
        }; // TODO clean up event listeners

        document.addEventListener('dblclick', doDrag2, false);
        document.addEventListener('mousemove', doDrag, false);
        document.addEventListener('mouseup', stopDrag, false);

        setClicked(true);
      
      }, [hoverPosition, width, height, blockProps, horizontal, resizeSteps, setClicked, setHeight, setWidth, vertical]);

      var styles = useMemo(function () {
        var _styles = {
          position: 'relative'
        };
        var isTop = hoverPosition.isTop,
          isLeft = hoverPosition.isLeft,
          isRight = hoverPosition.isRight,
          isBottom = hoverPosition.isBottom;

        if (horizontal === 'auto') {
          _styles.width = 'auto';
        } else if (horizontal === 'relative') {
          var value = width || blockProps.resizeData.width;

          if (!value && initialWidth) {
            _styles.width = initialWidth;
          } else {
            _styles.width = (value || 40) + "%";
          }
        } else if (horizontal === 'absolute') {
          var _value = width || blockProps.resizeData.width;

          if (!_value && initialWidth) {
            _styles.width = initialWidth;
          } else {
            _styles.width = (_value || 40) + "px";
          }
        }

        if (vertical === 'auto') {
          _styles.height = 'auto';
        } else if (vertical === 'relative') {
          var _value2 = height || blockProps.resizeData.height;

          if (!_value2 && initialHeight) {
            _styles.height = initialHeight;
          } else {
            _styles.height = (_value2 || 40) + "%";
          }
        } else if (vertical === 'absolute') {
          var _value3 = height || blockProps.resizeData.height;

          if (!_value3 && initialHeight) {
            _styles.height = initialHeight;
          } else {
            _styles.height = (_value3 || 40) + "%";
          }
        } // Handle cursor


        if (!isResizable) {
          _styles.cursor = 'default';
        } else if ((isRight && isBottom) || (isLeft && isTop)) {
          _styles.cursor = 'nwse-resize';
        } else if ((isRight && isTop) || (isBottom && isLeft)) {
          _styles.cursor = 'nesw-resize';
        } else if (isRight || isLeft) {
          _styles.cursor = 'ew-resize';
        } else if (isBottom || isTop) {
          _styles.cursor = 'ns-resize';
        } else {
          _styles.cursor = 'default';
        }

        return _styles;
      }, [hoverPosition, height, width, blockProps, horizontal, initialHeight, initialWidth, isResizable, vertical]);
      var interactionProps = !store.getReadOnly || store.getReadOnly() ? {} : {
        onMouseDown: mouseDown,
        onMouseMove: mouseMove,
        onMouseLeave: mouseLeave
      };
      return /*#__PURE__*/React.createElement(WrappedComponent, _extends({}, elementProps, interactionProps, {
        blockProps: blockProps,
        ref: function ref(node) {
          wrapper.current = node;

          if (typeof _ref3 === 'function') {
            _ref3(node);
          } else if (_ref3) {
            // eslint-disable-next-line no-param-reassign
            _ref3.current = node;
          }
        },
        style: styles
      }));
    });
    BlockResizeableDecorator.displayName = "BlockResizeable(" + getDisplayName(WrappedComponent) + ")"; // eslint-disable-next-line @typescript-eslint/no-explicit-any

    BlockResizeableDecorator.WrappedComponent = WrappedComponent.WrappedComponent || WrappedComponent;
    return BlockResizeableDecorator;
  };
});

var createSetResizeData = function createSetResizeData(contentBlock, _ref) {
  var getEditorState = _ref.getEditorState,
    setEditorState = _ref.setEditorState;
  return function (data) {
    var entityKey = contentBlock.getEntityAt(0);

    if (entityKey) {
      var editorState = getEditorState();
      var contentState = editorState.getCurrentContent();
      contentState.mergeEntityData(entityKey, _extends({}, data));
      setEditorState(EditorState.forceSelection(editorState, editorState.getSelection()));
    }
  };
};

var index = (function (config) {
  if (config === void 0) {
    config = {};
  }

  var store = {
    getEditorRef: undefined,
    getReadOnly: undefined,
    getEditorState: undefined,
    setEditorState: undefined
  };
  return {
    initialize: function initialize(_ref2) {
      var getEditorRef = _ref2.getEditorRef,
        getReadOnly = _ref2.getReadOnly,
        getEditorState = _ref2.getEditorState,
        setEditorState = _ref2.setEditorState;
      store.getReadOnly = getReadOnly;
      store.getEditorRef = getEditorRef;
      store.getEditorState = getEditorState;
      store.setEditorState = setEditorState;
    },
    decorator: createDecorator({
      config: config,
      store: store
    }),
    blockRendererFn: function blockRendererFn(contentBlock, _ref3) {
      var getEditorState = _ref3.getEditorState,
        setEditorState = _ref3.setEditorState;
      var entityKey = contentBlock.getEntityAt(0);
      var contentState = getEditorState().getCurrentContent();
      var resizeData = entityKey ? contentState.getEntity(entityKey).getData() : {};
      return {
        props: {
          resizeData: resizeData,
          setResizeData: createSetResizeData(contentBlock, {
            getEditorState: getEditorState,
            setEditorState: setEditorState
          })
        }
      };
    }
  };
});

export { index as default };
